import { Observable } from 'rxjs';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';

import UserApi from 'entities/user/api';
import OrganizationApi from 'entities/organization/api';
import { selectUser } from 'entities/user/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { STORE as STORE_USER } from 'entities/user/constants';

import {
  initSuccessAction,
  saveSuccessAction,
  saveFailedAction,
  cloneSuccessAction,
  cloneFailedAction,
} from './actions';
import {
  FORM_IDENTIFIER,
  DEMO_FORM_IDENTIFIER,
  INIT,
  SAVE,
  CLONE,
  CREATE_NEW_COMPANY,
  NEW_COMPANY_FORM_IDENTIFIER,
} from './constants';

const init = (action$, store) =>
  action$.ofType(INIT, STORE_USER).mergeMap(() => {
    const state = store.getState();
    const user = selectUser(state);
    const actions = Observable.concat([
      initSuccessAction(),
      initialize(FORM_IDENTIFIER, {
        email: user?.email,
        fullName: user?.fullName,
        organization: user?.organization?.id,
        displayCurrency: user?.displayCurrency || 'USD',
      }),
      initialize(DEMO_FORM_IDENTIFIER),
      initialize(NEW_COMPANY_FORM_IDENTIFIER),
    ]);
    return actions;
  });

const save = (action$, store) =>
  action$.ofType(SAVE).mergeMap(({ data }) => {
    const actions = [];
    const state = store.getState();
    const user = selectUser(state);

    const { newPassword, oldPassword } = data;
    if (newPassword) {
      if (!oldPassword) {
        return [showErrorAction('Please enter old password.')];
      }
      actions.push(UserApi.password({ newPassword, oldPassword }));
    }

    if (
      data.email !== user.email ||
      data.fullName.first !== user.fullName.first ||
      data.fullName.last !== user.fullName.last ||
      data.displayCurrency !== user.displayCurrency
    ) {
      actions.push(
        UserApi.update({
          email: data.email,
          fullName: data.fullName,
          displayCurrency: data.displayCurrency,
        })
      );
    }

    if (actions.length === 0) {
      return [];
    }

    return Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.combineLatest(actions)
        .mergeMap((resultingActions) => [
          ...resultingActions.filter(Boolean),
          showMessageAction('Changes Saved!'),
          saveSuccessAction(),
          stopSubmit(FORM_IDENTIFIER),
          initialize(FORM_IDENTIFIER, {
            email: data.email || user.email,
            fullName: data.fullName || user.fullName,
            displayCurrency: data.displayCurrency || 'USD',
          }),
        ])
        .catch((e) =>
          Observable.of(
            saveFailedAction(e),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('An error occurred during saving, please try again.')
          )
        )
    );
  });

const clone = (action$, store) =>
  action$.ofType(CLONE).mergeMap(({ data }) => {
    const actions = [];
    const { cloneCompanyName, cloneUserName, cloneUserEmail } = data;
    const state = store.getState();
    const user = selectUser(state);

    actions.push(
      OrganizationApi.cloneCompany(user.organization.id, {
        cloneCompanyName,
        cloneUserName,
        cloneUserEmail,
      })
    );

    return Observable.concat(
      Observable.of(startSubmit(DEMO_FORM_IDENTIFIER)),
      Observable.combineLatest(actions)
        .mergeMap(() => [
          cloneSuccessAction(),
          stopSubmit(DEMO_FORM_IDENTIFIER),
          showMessageAction('Demo Company and user successfully created!'),
        ])
        .catch((e) =>
          Observable.of(
            cloneFailedAction(e),
            stopSubmit(DEMO_FORM_IDENTIFIER),
            showErrorAction(`An error occurred during saving, please try again. ${e}`)
          )
        )
    );
  });

const createNewCompany = (action$, store) =>
  action$.ofType(CREATE_NEW_COMPANY).mergeMap(({ data }) => {
    const actions = [];
    const {
      newCompanyName,
      newCompanyUserName,
      newCompanyUserEmail,
      companyAccountCurrency,
      enableCompanyGivingAccount,
    } = data;
    const state = store.getState();
    const user = selectUser(state);

    actions.push(
      OrganizationApi.createNewCompany(user.organization.id, {
        newCompanyName,
        newCompanyUserName,
        newCompanyUserEmail,
        companyAccountCurrency,
        enableCompanyGivingAccount,
      })
    );

    return Observable.concat(
      Observable.of(startSubmit(NEW_COMPANY_FORM_IDENTIFIER)),
      Observable.combineLatest(actions)
        .mergeMap(() => [
          cloneSuccessAction(),
          stopSubmit(NEW_COMPANY_FORM_IDENTIFIER),
          showMessageAction('New Company and user successfully created!'),
          initialize(NEW_COMPANY_FORM_IDENTIFIER),
        ])
        .catch((e) =>
          Observable.of(
            cloneFailedAction(e),
            stopSubmit(NEW_COMPANY_FORM_IDENTIFIER),
            showErrorAction(`An error occurred during saving, please try again. ${e}`)
          )
        )
    );
  });

export default [init, save, clone, createNewCompany];
